import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const PWAInstallPrompt = ({ onDismiss, userName }) => {
  const [deviceType, setDeviceType] = useState('unknown');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Detect device type
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setDeviceType('ios');
    } else if (/android/.test(userAgent)) {
      setDeviceType('android');
    } else {
      setDeviceType('desktop');
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for beforeinstallprompt event (Chrome/Android)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa_install_prompt_dismissed', 'true');
    localStorage.setItem('pwa_install_prompt_date', new Date().toISOString());
    onDismiss();
  };

  if (isInstalled) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-5 rounded-t-2xl relative">
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-2xl">🏆</span>
            </div>
            <div className="text-white">
              <h2 className="text-xl font-bold">Add ClubHudl to Home Screen</h2>
              <p className="text-green-100 text-sm">Quick access like a real app!</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Benefits - Compact */}
          <div className="grid grid-cols-2 gap-2 mb-5">
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <span className="text-lg">⚡</span>
              <span className="text-sm font-medium text-gray-700">Instant Access</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <span className="text-lg">🔔</span>
              <span className="text-sm font-medium text-gray-700">Notifications</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <span className="text-lg">📱</span>
              <span className="text-sm font-medium text-gray-700">Full Screen</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <span className="text-lg">🌐</span>
              <span className="text-sm font-medium text-gray-700">Works Offline</span>
            </div>
          </div>

          {/* Native install button (Android Chrome) */}
          {deferredPrompt && (
            <>
              <button
                onClick={handleInstallClick}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all mb-3"
              >
                📲 Install ClubHudl Now
              </button>
              <button
                onClick={handleDismiss}
                className="w-full text-gray-500 py-2 font-medium hover:text-gray-700"
              >
                Maybe Later
              </button>
            </>
          )}

          {/* Manual install instructions */}
          {!deferredPrompt && (
            <>
              {/* iOS Instructions */}
              {deviceType === 'ios' && (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    <p className="text-sm text-blue-800 font-medium">
                      🍎 Use Safari browser for these steps
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-sm">1</div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Tap the Share button</p>
                        <p className="text-xs text-gray-500">📤 At the bottom of Safari</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-sm">2</div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Add to Home Screen</p>
                        <p className="text-xs text-gray-500">➕ Scroll down and tap this option</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-sm">3</div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Tap "Add"</p>
                        <p className="text-xs text-gray-500">✅ Confirm in top right corner</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Android Instructions */}
              {deviceType === 'android' && (
                <div className="space-y-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <p className="text-sm text-green-800 font-medium">
                      🤖 Use Chrome browser for these steps
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-sm">1</div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Tap menu ⋮</p>
                        <p className="text-xs text-gray-500">Three dots in top right corner</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-sm">2</div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Install app</p>
                        <p className="text-xs text-gray-500">📲 Or "Add to Home screen"</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-sm">3</div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Tap "Install"</p>
                        <p className="text-xs text-gray-500">✅ Confirm the installation</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Desktop Instructions */}
              {deviceType === 'desktop' && (
                <div className="space-y-3">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                    <p className="text-sm text-purple-800 font-medium">
                      💻 Use Chrome or Edge browser
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-sm">1</div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Look for install icon</p>
                        <p className="text-xs text-gray-500">📥 In the address bar (right side)</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-sm">2</div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Click "Install"</p>
                        <p className="text-xs text-gray-500">✅ Confirm the installation</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="mt-5 space-y-2">
                <button
                  onClick={handleDismiss}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
                >
                  Got It!
                </button>
                <button
                  onClick={handleDismiss}
                  className="w-full text-gray-500 py-2 text-sm hover:text-gray-700"
                >
                  Maybe Later
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-5 py-3 rounded-b-2xl border-t">
          <p className="text-xs text-gray-500 text-center">
            Always available at clubhudl.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;